import React, { useContext, useEffect } from 'react'
import '../FinancialInfo/styles.scss'
import 'chartjs-plugin-datalabels'
import usePortfolioName from '../../../../../hooks/usePortfolioName';
import { TagFilterContext } from '../../../../../context/TagFilterContext';
import HeaderExtended from '../HeaderExtended';
import { useTranslation } from 'react-i18next';
import { FinancialInfoData } from '../GeneralInformation/typesExtendedReport';
import { useFinancialMappedData } from './useFinancialMappedData';
import LastFiveYears from './components/LastFiveYears';
import LatestDisbursedProjects from './components/LatestDisbursedProjects';
import EfasDistribution from './components/EfasDistribution';
import ReusableIcon from '../shared/ReusableIcon';


type FinancialProps = {
    data: FinancialInfoData | null | undefined;
    isLoan?: boolean;
    isTcp?: boolean;
    isIgr?: boolean;
    creationDate?: Date;
    regions?: any;
    countries?: string;
    myPortfolio?: string;
    filtersApplied?: any;
}

const COLORS = {
    clean: '#91E2A1',
    qualified: '#F7DD00',
    adverse: '#F06D6D',
    abstention: '#EF9206',
    pending: '#D0DEF8',
}

const options = {
    cutoutPercentage: 83,
    responsive: true,
    maintainAspectRatio: false,
    legend: {
        display: false
    },
    plugins: {
        datalabels: false
    }
};

const FinancialInfo: React.FC<FinancialProps> = ({ data, isLoan, isTcp, isIgr, creationDate, regions, countries, myPortfolio, filtersApplied }) => {
    const { t } = useTranslation();
    const portfolioName = usePortfolioName();
    const { labels } = useContext(TagFilterContext);
    const titleLon = 'LON';
    const titleTcp = 'TCP';
    const titleIgr = 'IGR';
    const mp = useFinancialMappedData(data);

    if (!data || !mp) return null;

    const efaLegend = [
        { label: `${t('extended_report.clean')}`, value: mp.efas.clean, percent: mp.efas.cleanPercent, color: COLORS.clean },
        { label: `${t('extended_report.qualified_opinion')}`, value: mp.efas.qualifiedOpinion, percent: mp.efas.qualifiedOpinionPercent, color: COLORS.qualified },
        { label: `${t('extended_report.adverse_opinion')}`, value: mp.efas.adverseOpinion, percent: mp.efas.adverseOpinionPercent, color: COLORS.adverse },
        { label: `${t('extended_report.abstention_opinion')}`, value: mp.efas.abstentionOpinion, percent: mp.efas.abstentionOpinionPercent, color: COLORS.abstention },
        { label: `${t('extended_report.pending')}`, value: mp.efas.pending, percent: mp.efas.pendingPercent, color: COLORS.pending },
    ];

    const chartData = mp.Last5Years.map(
        ({ year, originalProjectionAmount, disbursementAmount }) => ({
            year,
            projected: originalProjectionAmount,
            disbursed: disbursementAmount,
        })
    );

    const doughnutData = {
        labels: [],
        datasets: [
            {
                data: [
                    mp.efas.cleanPercent || 0,
                    mp.efas.qualifiedOpinionPercent || 0,
                    mp.efas.adverseOpinionPercent || 0,
                    mp.efas.abstentionOpinionPercent || 0,
                    mp.efas.pendingPercent || 0
                ],
                backgroundColor: [COLORS.clean, COLORS.qualified, COLORS.adverse, COLORS.abstention, COLORS.pending],
                hoverBackgroundColor: [COLORS.clean, COLORS.qualified, COLORS.adverse, COLORS.abstention, COLORS.pending],
            },
        ],
    };

    return (
        <>
            <div className='c-main' style={{ padding: '45px' }}>
                <HeaderExtended
                    portfolioName={portfolioName}
                    filtersApplied={filtersApplied ?? labels}
                    creationDate={creationDate}
                    regions={regions}
                    countries={countries}
                    myPortfolio={myPortfolio}
                />
                <div className='content-financial'>
                    <header className="header-content content-extended-header mt-2">
                        <p>{t('one_pager_report.financial_information')}</p>
                        <div className='flex-avatars'>
                            {(isLoan) && (<ReusableIcon title={titleLon} color="#BE86D8" />)}
                            {(isTcp) && (<ReusableIcon title={titleTcp} color="#66B6B6" />)}
                            {(isIgr) && (<ReusableIcon title={titleIgr} color="#F1AF43" />)}
                        </div>
                    </header>

                    <div className='grid-financial'>
                        <section className='panel-full panel-financial'>
                            <LastFiveYears
                                chartData={chartData}
                            />
                        </section>

                        <section className='panel-financial'>
                            <LatestDisbursedProjects
                                mp={mp}
                                isLoan={isLoan}
                                isTcp={isTcp}
                                isIgr={isIgr}
                                titleLon={titleLon}
                                titleTcp={titleTcp}
                                titleIgr={titleIgr}
                            />
                        </section>

                        <section className='panel-financial'>
                            <EfasDistribution
                                efaLegend={efaLegend}
                                doughnutData={doughnutData}
                                options={options}
                            />
                        </section>
                    </div>
                </div>
            </div>
        </>
    )
}

export default FinancialInfo

